import { useContext, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isCommentTooLongError, showCommentTooLongAlert } from '../../../utils/commentErrorUtils';
import { Box } from 'foundations-components/transitional/components/Box';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { useFetchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { REPLY } from '../../../types/CommentTypes';
import { CommentsContext } from '../../../contexts/CommentsContext';
import { CommentRTE } from './CommentRTE';
import { USER } from '../../../constants/CallParticipantTypes';
import { AvatarWrapper } from '../../shared/AvatarWrapper';
import { getReplyCommentStorageKey } from '../../../constants/LocalStorageConstants';
import { useLocalStorageState } from '../../../utils/useLocalStorageState';
import debounce from 'react-utils/debounce';
import { AVATAR_SIZE_PX } from '../../../constants/AvatarSizes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY_REPLY_MESSAGE = '<div style="" dir="auto" data-top-level="true"><br></div>';
export const ReplyEditor = props => {
  const {
    repliedToComment,
    onCancelCallback,
    onSubmitCallback
  } = props;
  const [isEditing, setIsEditing] = useState(true);
  const {
    currentUserId,
    handleAddComment,
    commentingNotificationTitle,
    commentingNotificationCtaUrl,
    onError,
    avatarConfig: {
      size: avatarSize,
      flushInline
    }
  } = useContext(CommentsContext);
  const resolver = createStandardCachedResolver(USER);
  const {
    status,
    reference
  } = useFetchResolver(resolver, currentUserId ? currentUserId.toString() : '');
  const name = status === 'success' && reference ? reference.label : '';
  const email = status === 'success' && reference ? reference.description : '';
  const parentCommentId = repliedToComment.type === REPLY && repliedToComment.parentId ? repliedToComment.parentId : repliedToComment.id;
  const replyCommentStorageKey = getReplyCommentStorageKey({
    objectType: repliedToComment.objectType,
    objectId: repliedToComment.objectId,
    parentCommentId
  });
  const [replyCommentDraft, setReplyCommentDraft] = useLocalStorageState({
    key: replyCommentStorageKey,
    initialValue: undefined
  });
  const handleReplyBtnClick = (message, atMentionedUserIds, mentionCounts) => {
    const newCommentPayload = {
      type: REPLY,
      parentId: parentCommentId,
      message,
      atMentionedUserIds,
      metaDescription: repliedToComment.metaDescription,
      commentedOnObject: commentingNotificationTitle ? {
        title: commentingNotificationTitle,
        heading: '',
        body: ''
      } : undefined,
      ctaUrl: commentingNotificationCtaUrl,
      shouldSendNotification: true,
      objectId: repliedToComment.objectId,
      objectType: repliedToComment.objectType,
      mentionCounts
    };
    setReplyCommentDraft(undefined);
    setIsEditing(false);
    handleAddComment(newCommentPayload).then(() => {
      onSubmitCallback();
    }).catch(error => {
      if (isCommentTooLongError(error)) {
        showCommentTooLongAlert();
      } else {
        onError( /*#__PURE__*/_jsx(FormattedMessage, {
          message: "objectCommentsThread.comments.alerts.replyError"
        }));
      }
    });
  };
  const handleCancelBtnClick = () => {
    onCancelCallback();
    setReplyCommentDraft(undefined);
    setIsEditing(false);
  };
  const handleChange = debounce(value => {
    if (value !== EMPTY_REPLY_MESSAGE && isEditing) {
      setReplyCommentDraft(value);
    }
  }, 300);
  return /*#__PURE__*/_jsxs(TFlex, {
    "data-test-id": "comments-replyEditorContainer",
    paddingBlock: "400",
    paddingInline: flushInline ? '200' : '300',
    children: [/*#__PURE__*/_jsx(Box, {
      grow: 0,
      shrink: 0,
      basis: AVATAR_SIZE_PX[avatarSize],
      marginInlineEnd: flushInline ? '200' : '300',
      children: /*#__PURE__*/_jsx(AvatarWrapper, {
        objectId: currentUserId,
        email: email,
        displayName: name,
        objectType: USER,
        size: avatarSize
      })
    }), /*#__PURE__*/_jsx(Box, {
      grow: 1,
      basis: "100%",
      children: /*#__PURE__*/_jsx(CommentRTE, {
        draftValue: replyCommentDraft,
        onSubmitClick: handleReplyBtnClick,
        onCancelClick: handleCancelBtnClick,
        submitBtnText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "objectCommentsThread.comments.replyBtn"
        }),
        onChange: handleChange,
        autoFocus: true
      })
    })]
  });
};